import React, { useState, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm, Controller } from 'react-hook-form';
import {
  ArrowLeftIcon,
  CloudArrowUpIcon,
  DocumentIcon,
  PhotoIcon,
  VideoCameraIcon,
  XMarkIcon,
  PlusIcon
} from '@heroicons/react/24/outline';
import { resourcesAPI, clientsAPI, facilitiesAPI, placementsAPI } from '../../services/api';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import { useToast } from '../../context/ToastContext';

interface UploadFormData {
  relatedToType: string;
  relatedToId: string;
  category: string;
  description?: string;
  tags: string;
  isConfidential: boolean;
  accessLevel: string;
  expirationDate?: string;
}

const ResourceUpload: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { success: showSuccess, error: showError } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [dragActive, setDragActive] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    control,
    formState: { errors, isSubmitting }
  } = useForm<UploadFormData>({
    defaultValues: {
      relatedToType: 'Client',
      category: 'Other',
      isConfidential: false,
      accessLevel: 'internal',
      tags: ''
    }
  });

  const relatedToType = watch('relatedToType');

  // Fetch related objects for dropdown
  const {
    data: clientsData,
    isLoading: isLoadingClients
  } = useQuery({
    queryKey: ['clients'],
    queryFn: () => clientsAPI.getClients(),
    enabled: relatedToType === 'Client',
  });

  const {
    data: facilitiesData,
    isLoading: isLoadingFacilities
  } = useQuery({
    queryKey: ['facilities'],
    queryFn: () => facilitiesAPI.getFacilities(),
    enabled: relatedToType === 'Facility',
  });

  const {
    data: placementsData,
    isLoading: isLoadingPlacements
  } = useQuery({
    queryKey: ['placements'],
    queryFn: () => placementsAPI.getPlacements(),
    enabled: relatedToType === 'Placement',
  });

  const clients = clientsData?.data?.data?.clients || [];
  const facilities = facilitiesData?.data?.data?.facilities || [];
  const placements = placementsData?.data?.data?.placements || [];

  // Upload mutation
  const uploadMutation = useMutation({
    mutationFn: async (data: UploadFormData) => {
      const results = [];
      
      for (const file of selectedFiles) {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('relatedToType', data.relatedToType);
        formData.append('relatedToId', data.relatedToId);
        formData.append('category', data.category);
        formData.append('isConfidential', data.isConfidential.toString());
        formData.append('accessLevel', data.accessLevel);
        
        if (data.description) {
          formData.append('description', data.description);
        }
        
        if (data.expirationDate) {
          formData.append('expirationDate', data.expirationDate);
        }
        
        if (data.tags) {
          const tagsArray = data.tags.split(',').map(tag => tag.trim()).filter(tag => tag);
          formData.append('tags', tagsArray.join(','));
        }

        const result = await resourcesAPI.uploadResource(formData);
        results.push(result);
      }
      
      return results;
    },
    onSuccess: (results) => {
      showSuccess(`${results.length} file${results.length > 1 ? 's' : ''} uploaded successfully!`);
      queryClient.invalidateQueries({ queryKey: ['resources'] });
      navigate('/resources');
    },
    onError: (error: any) => {
      showError(error.response?.data?.error || 'Failed to upload files');
    },
  });

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    const files = Array.from(e.dataTransfer.files);
    handleFiles(files);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const files = Array.from(e.target.files);
      handleFiles(files);
    }
  };

  const handleFiles = (files: File[]) => {
    const allowedTypes = [
      'image/jpeg',
      'image/jpg', 
      'image/png',
      'image/gif',
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'text/plain',
      'text/csv'
    ];

    const validFiles = files.filter(file => {
      if (!allowedTypes.includes(file.type)) {
        showError(`File type not allowed: ${file.name}`);
        return false;
      }
      
      // 50MB limit
      if (file.size > 50 * 1024 * 1024) {
        showError(`File too large: ${file.name}. Maximum size is 50MB.`);
        return false;
      }
      
      return true;
    });

    setSelectedFiles(prev => [...prev, ...validFiles]);
  };

  const removeFile = (index: number) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const getFileIcon = (file: File) => {
    if (file.type.startsWith('image/')) {
      return <PhotoIcon className="w-5 h-5 text-green-600" />;
    } else if (file.type.startsWith('video/')) {
      return <VideoCameraIcon className="w-5 h-5 text-blue-600" />;
    } else {
      return <DocumentIcon className="w-5 h-5 text-gray-600" />;
    }
  };

  const formatFileSize = (bytes: number) => {
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    if (bytes === 0) return '0 Bytes';
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round((bytes / Math.pow(1024, i)) * 100) / 100 + ' ' + sizes[i];
  };

  const onSubmit = (data: UploadFormData) => {
    if (selectedFiles.length === 0) {
      showError('Please select at least one file to upload');
      return;
    }
    
    uploadMutation.mutate(data);
  };

  if (isLoadingClients || isLoadingFacilities || isLoadingPlacements) {
    return <LoadingSpinner />;
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center space-x-4">
          <Link
            to="/resources"
            className="inline-flex items-center text-gray-600 hover:text-gray-900"
          >
            <ArrowLeftIcon className="w-4 h-4 mr-2" />
            Back to Documents
          </Link>
        </div>
        <div className="mt-4">
          <h1 className="text-2xl font-bold text-gray-900 text-left">Upload Documents</h1>
          <p className="text-gray-600 mt-2">
            Upload files and associate them with clients, facilities, or placements
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* File Upload Area */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Select Files</h3>
          
          {/* Drop Zone */}
          <div
            className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
              dragActive
                ? 'border-primary-400 bg-primary-50'
                : 'border-gray-300 hover:border-gray-400'
            }`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            <CloudArrowUpIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h4 className="text-lg font-medium text-gray-900 mb-2">
              Drop files here or click to select
            </h4>
            <p className="text-gray-600 mb-4">
              Supports: PDF, Word, Excel, Images (JPG, PNG, GIF), Text files
            </p>
            <p className="text-sm text-gray-500 mb-4">
              Maximum file size: 50MB per file
            </p>
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
            >
              <PlusIcon className="w-4 h-4 mr-2" />
              Select Files
            </button>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              onChange={handleFileSelect}
              className="hidden"
              accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif,.txt,.csv"
            />
          </div>

          {/* Selected Files */}
          {selectedFiles.length > 0 && (
            <div className="mt-6">
              <h4 className="text-md font-medium text-gray-900 mb-3">
                Selected Files ({selectedFiles.length})
              </h4>
              <div className="space-y-2">
                {selectedFiles.map((file, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-3 bg-gray-50 border border-gray-200 rounded-lg"
                  >
                    <div className="flex items-center space-x-3">
                      {getFileIcon(file)}
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {file.name}
                        </div>
                        <div className="text-xs text-gray-500">
                          {formatFileSize(file.size)}
                        </div>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeFile(index)}
                      className="text-gray-400 hover:text-red-600"
                    >
                      <XMarkIcon className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Document Information */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Document Information</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Related To Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Related To *
              </label>
              <select
                {...register('relatedToType', { required: 'Related to type is required' })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="Client">Client</option>
                <option value="Facility">Facility</option>
                <option value="Placement">Placement</option>
                <option value="Task">Task</option>
              </select>
              {errors.relatedToType && (
                <p className="mt-1 text-sm text-red-600">{errors.relatedToType.message}</p>
              )}
            </div>

            {/* Related To Object */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Select {relatedToType} *
              </label>
              <select
                {...register('relatedToId', { required: 'Related object is required' })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="">Select {relatedToType.toLowerCase()}...</option>
                {relatedToType === 'Client' && clients.map((client: any) => (
                  <option key={client._id} value={client._id}>
                    {client.firstName} {client.lastName}
                  </option>
                ))}
                {relatedToType === 'Facility' && facilities.map((facility: any) => (
                  <option key={facility._id} value={facility._id}>
                    {facility.name} - {facility.address?.city}, {facility.address?.state}
                  </option>
                ))}
                {relatedToType === 'Placement' && placements.map((placement: any) => (
                  <option key={placement._id} value={placement._id}>
                    {placement.client?.firstName} {placement.client?.lastName} → {placement.facility?.name}
                  </option>
                ))}
              </select>
              {errors.relatedToId && (
                <p className="mt-1 text-sm text-red-600">{errors.relatedToId.message}</p>
              )}
            </div>

            {/* Category */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Category *
              </label>
              <select
                {...register('category', { required: 'Category is required' })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="Medical Records">Medical Records</option>
                <option value="Insurance Documents">Insurance Documents</option>
                <option value="Legal Documents">Legal Documents</option>
                <option value="Identification">Identification</option>
                <option value="Photos">Photos</option>
                <option value="Assessments">Assessments</option>
                <option value="Care Plans">Care Plans</option>
                <option value="Contracts">Contracts</option>
                <option value="Facility Brochures">Facility Brochures</option>
                <option value="Licenses">Licenses</option>
                <option value="Inspection Reports">Inspection Reports</option>
                <option value="Other">Other</option>
              </select>
              {errors.category && (
                <p className="mt-1 text-sm text-red-600">{errors.category.message}</p>
              )}
            </div>

            {/* Access Level */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Access Level *
              </label>
              <select
                {...register('accessLevel', { required: 'Access level is required' })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="public">Public</option>
                <option value="internal">Internal</option>
                <option value="restricted">Restricted</option>
              </select>
              {errors.accessLevel && (
                <p className="mt-1 text-sm text-red-600">{errors.accessLevel.message}</p>
              )}
            </div>

            {/* Expiration Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Expiration Date
              </label>
              <input
                type="date"
                {...register('expirationDate')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>

            {/* Confidential */}
            <div className="flex items-center">
              <input
                type="checkbox"
                {...register('isConfidential')}
                className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
              />
              <label className="ml-2 text-sm font-medium text-gray-700">
                Mark as Confidential
              </label>
            </div>
          </div>

          {/* Description */}
          <div className="mt-6">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              {...register('description')}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              placeholder="Describe the document contents..."
            />
          </div>

          {/* Tags */}
          <div className="mt-6">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Tags
            </label>
            <input
              type="text"
              {...register('tags')}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              placeholder="Enter tags separated by commas..."
            />
            <p className="mt-1 text-sm text-gray-500">
              Add tags to help organize and find documents later (e.g., urgent, signed, reviewed)
            </p>
          </div>
        </div>

        {/* Form Actions */}
        <div className="flex items-center justify-end space-x-4">
          <Link
            to="/resources"
            className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
          >
            Cancel
          </Link>
          <button
            type="submit"
            disabled={isSubmitting || selectedFiles.length === 0}
            className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting 
              ? `Uploading ${selectedFiles.length} file${selectedFiles.length > 1 ? 's' : ''}...`
              : `Upload ${selectedFiles.length} file${selectedFiles.length > 1 ? 's' : ''}`
            }
          </button>
        </div>
      </form>
    </div>
  );
};

export default ResourceUpload;
